/**
 * The reference logo wall.
 *
 * `/referenzen` stores its eight client and partner marks as eight consecutive
 * `image` blocks pointing at 190x70 rasters. The original group replaces the
 * non-Company-Move part of that run, one mark for one mark, with the official
 * vector assets normalised by
 * `scripts/logos.mjs`. The manifest it writes carries a per-logo size solved so
 * every mark puts the same amount of ink on the page, and a vertical nudge that
 * puts each mark's optical centre, not its bounding box, on the cell's centre.
 *
 * Deliberate constraints, from the brand research in
 * `research/04-reference-logos.md`:
 *   - Full official colour on white. No `filter: grayscale()`, no opacity fade,
 *     no hover recolour: a modified mark is an unlicensed mark, and Samsung
 *     sanctions only Blue, Black and White.
 *   - Never clickable, and the `alt` names the company and nothing else, so the
 *     wall makes no claim of partnership, endorsement or certification.
 *   - Proportions are never altered: every asset is trimmed to its own ink box
 *     and drawn at its intrinsic aspect ratio.
 */
import type { CSSProperties } from 'react';
import logosData from '@/content/logos.json';
import type { ImageBlockData } from '@/lib/content';
import { UI } from '@/lib/ui-strings';

export type Logo = {
  name: string;
  /** The company name, used verbatim as the image's alt text. */
  label: string;
  /** The /media raster on the source page that this mark replaces. */
  legacy: string;
  src: string;
  kind: 'vector' | 'raster';
  aspect: number;
  /** Solved size inside the manifest's cell, in CSS pixels. */
  width: number;
  height: number;
  /** Optical centring offset, in CSS pixels. Positive moves the mark down. */
  shiftY: number;
  webp?: { x1: string; x2: string };
};

type Manifest = { cell: { w: number; h: number }; logos: Logo[] };

const manifest = logosData as unknown as Manifest;

const byLegacy = new Map(manifest.logos.map((logo) => [logo.legacy, logo]));

/** Resolve one of the legacy source-page image paths to its vector record. */
export function logoForLegacy(src: string | undefined): Logo | undefined {
  return src ? byLegacy.get(src) : undefined;
}

/**
 * The logos this run of image blocks is, or null if it is an ordinary group.
 *
 * Matching on the source image path rather than on position keeps the wall
 * bound to the page's own data: the marks appear in the order the page lists
 * them, and a run that is not the logo wall is left to render as images.
 */
export function logoWallFor(blocks: ImageBlockData[]): Logo[] | null {
  if (blocks.length < 4) return null;
  const found: Logo[] = [];
  const seen = new Set<string>();
  for (const block of blocks) {
    const logo = logoForLegacy(block.image?.src);
    if (!logo || seen.has(logo.name)) return null;
    seen.add(logo.name);
    found.push(logo);
  }
  return found;
}

type Props = {
  logos: Logo[];
  additionalLogos?: Pick<Logo, 'name' | 'label' | 'src' | 'kind'>[];
  /** The wall is above the fold on /referenzen; its marks total under 20 KB. */
  eager?: boolean;
};

export default function LogoWall({ logos, additionalLogos = [], eager = false }: Props) {
  const { cell } = manifest;
  const seen = new Set(logos.map((logo) => logo.name));
  const combined = [...logos, ...additionalLogos.filter((logo) => {
    if (seen.has(logo.name)) return false;
    seen.add(logo.name);
    return true;
  })];

  return (
    <div className="hp-block hp-logo-wall">
      <ul className="hp-logo-wall__grid" aria-label={UI.logoWall} data-reveal-group>
        {combined.map((logo, index) => (
          <li
            className="hp-logo-wall__cell"
            key={logo.name}
            style={{ '--i': index } as CSSProperties}
          >
            <div className="hp-logo-wall__box">
              <Mark logo={logo} cell={cell} eager={eager} />
            </div>
          </li>
        ))}
      </ul>
      <p className="hp-logo-wall__note">{UI.logoWallNote}</p>
    </div>
  );
}

function Mark({
  logo,
  cell,
  eager,
}: {
  logo: Logo | Pick<Logo, 'name' | 'label' | 'src' | 'kind'>;
  cell: { w: number; h: number };
  eager: boolean;
}) {
  if (!('width' in logo)) {
    return (
      <img
        className="hp-logo-wall__img hp-logo-wall__img--additional"
        src={logo.src}
        alt={logo.label}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
      />
    );
  }
  // Sized as a share of the cell rather than in pixels, so the whole wall
  // scales down together and the optical balance survives every viewport.
  const style = {
    '--logo-w': `${((logo.width / cell.w) * 100).toFixed(3)}%`,
    '--logo-shift': (logo.shiftY / logo.height).toFixed(4),
  } as CSSProperties;

  const img = (
    <img
      className="hp-logo-wall__img"
      src={logo.src}
      alt={logo.label}
      width={Math.round(logo.width)}
      height={Math.round(logo.height)}
      style={style}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      draggable={false}
    />
  );

  if (logo.kind === 'raster' && logo.webp) {
    return (
      <picture className="hp-logo-wall__picture">
        <source type="image/webp" srcSet={`${logo.webp.x1} 1x, ${logo.webp.x2} 2x`} />
        {img}
      </picture>
    );
  }
  return img;
}
